import { useState, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Menu,
} from 'lucide-react';
import { cn } from '@xanectech/ui/lib';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@xanectech/ui/components';
import { useIsMobile } from '@xanectech/ui/lib';
import type {
  NavGroup,
  NavItem,
  BrandingConfig,
  UserInfo,
} from './dashboard-config';
import { createLogger } from '@xanectech/lib';
import { DashboardConfig } from './dashboard-config';

const logger = createLogger('DashboardSidebar');

interface DashboardSidebarProps extends DashboardConfig {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

function getActiveHref(
  navigation: NavGroup[],
  pathname: string,
): string | null {
  let activeHref: string | null = null;
  let maxLength = 0;

  navigation.forEach((group) => {
    group.items.forEach((item) => {
      if (
        pathname === item.href ||
        (pathname.startsWith(item.href + '/') && item.href.length > 1)
      ) {
        if (item.href.length > maxLength) {
          maxLength = item.href.length;
          activeHref = item.href;
        }
      }
    });
  });

  return activeHref;
}

// Shared sidebar content component
function SidebarContent({
  branding,
  navigation,
  basePath,
  user,
  onLogout,
  collapsed = false,
  onCollapsedChange,
  onLinkClick,
  singleExpanded,
}: {
  branding: BrandingConfig;
  navigation: NavGroup[];
  basePath: string;
  user?: UserInfo;
  onLogout?: () => void;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onLinkClick?: () => void;
  singleExpanded?: boolean;
}) {
  const location = useLocation();

  // State for expanded/collapsed navgroups
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    navigation
      .map((n) => {
        return {
          [n.label]: !n.collapsable
            ? true
            : singleExpanded
              ? false
              : n.expanded === undefined
                ? true
                : n.expanded,
        };
      })
      .reduce((acc, curr) => ({ ...acc, ...curr })),
  );

  const handleGroupToggle = (label: string) => {
    const group = navigation.find((g) => g.label === label);
    if (group?.collapsable === false) {
      return; // Do nothing if group is not collapsable
    }
    if (singleExpanded) {
      // Collapse all other groups
      const newState: Record<string, boolean> = {};
      navigation.forEach((group) => {
        newState[group.label] =
          group.label === label
            ? !expandedGroups[label]
            : group.collapsable
              ? false
              : true;
      });
      setExpandedGroups(newState);
      return;
    }
    setExpandedGroups((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const activeHref = getActiveHref(navigation, location.pathname);
  const isActive = (href: string) => href === activeHref;
  // const isActive = (href: string) => {
  //   if (href === '/' || href === basePath) {
  //     return location.pathname === href;
  //   }

  //   return (
  //     location.pathname === href || location.pathname.startsWith(href + '/')
  //   );
  // };

  const NavItemComponent = ({ item }: { item: NavItem }) => {
    const content = (
      <NavLink
        to={item.href}
        onClick={onLinkClick}
        className={cn(
          'flex items-center rounded-lg py-2.5 text-sm font-medium transition-all duration-200',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          collapsed ? 'justify-center px-2' : 'gap-3 px-3',
          isActive(item.href)
            ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
            : 'text-sidebar-foreground/70',
        )}
        state={{ showBreadcrumbs: item.showBreadcrumbs ?? true }}
      >
        <item.icon className='h-5 w-5 shrink-0' />
        {!collapsed && (
          <>
            <span className='flex-1'>{item.title}</span>
            {item.badge !== undefined && (
              <span className='flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-destructive-foreground'>
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side='right' className='flex items-center gap-2'>
            {item.title}
            {item.badge !== undefined && (
              <span className='flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-destructive-foreground'>
                {item.badge}
              </span>
            )}
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <>
      {/* Logo */}
      <div className='flex h-16 items-center justify-between border-b border-sidebar-border px-4'>
        {!collapsed &&
          (branding.logo ? (
            <div className='flex items-center justify-start gap-2'>
              <img src={branding.logo} alt={branding.name} className='h-8' />
              <span className='text-lg font-bold text-sidebar-foreground'>
                {branding.name}
              </span>
            </div>
          ) : (
            <span className='text-lg font-bold text-sidebar-foreground'>
              {branding.name}
            </span>
          ))}
        {collapsed && branding.logoCollapsed && (
          <img
            src={branding.logoCollapsed}
            alt={branding.name}
            className='h-8'
          />
        )}
      </div>

      {/* Navigation */}
      <nav className='flex-1 space-y-1 overflow-y-auto p-3'>
        {navigation.map((group, groupIndex) => {
          // Determine expanded state: default to group.expanded or true if not set
          const isExpanded =
            expandedGroups[group.label] ?? group.expanded ?? true;
          return (
            <div key={group.label} className='space-y-1'>
              {!collapsed && (
                <div
                  className={cn(
                    groupIndex > 0 ? 'mt-5' : '',
                    'mb-1 px-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50',
                  )}
                >
                  <span>{group.label}</span>
                  {group.collapsable && (
                    <button
                      type='button'
                      onClick={() => handleGroupToggle(group.label)}
                      className='ml-2'
                      aria-label={
                        isExpanded ? 'Collapse group' : 'Expand group'
                      }
                    >
                      {isExpanded ? (
                        <ChevronDown className='h-4 w-4' />
                      ) : (
                        <ChevronRight className='h-4 w-4' />
                      )}
                    </button>
                  )}
                </div>
              )}
              {isExpanded &&
                group.items.map((item) => (
                  <NavItemComponent key={item.href} item={item} />
                ))}
            </div>
          );
        })}
      </nav>

      {/* User Section */}
      {user && (
        <div className='border-t border-sidebar-border p-3'>
          <div
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2',
              collapsed && 'justify-center',
            )}
          >
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className='h-8 w-8 shrink-0 rounded-full object-cover'
              />
            ) : (
              <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-primary text-sidebar-primary-foreground'>
                <span className='text-sm font-semibold'>{user.initials}</span>
              </div>
            )}
            {!collapsed && (
              <div className='flex-1 overflow-hidden'>
                <p className='truncate text-sm font-medium text-sidebar-foreground'>
                  {user.name}
                </p>
                {user.role && (
                  <p className='truncate text-xs text-sidebar-foreground/60'>
                    {user.role}
                  </p>
                )}
              </div>
            )}
            {!collapsed && onLogout && (
              <Button
                variant='ghost'
                size='icon'
                onClick={onLogout}
                className='h-8 w-8 shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-destructive'
              >
                <LogOut className='h-4 w-4' />
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Mobile trigger button component
function MobileSidebarTrigger({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant='ghost'
      size='icon'
      onClick={onClick}
      className='md:hidden h-9 w-9'
    >
      <Menu className='h-5 w-5' />
      <span className='sr-only'>Open menu</span>
    </Button>
  );
}

function DashboardSidebar({
  branding,
  navigation,
  basePath,
  user,
  onLogout,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  mobileOpen = false,
  onMobileOpenChange,
  singleExpanded,
}: DashboardSidebarProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = controlledCollapsed ?? internalCollapsed;
  const setCollapsed = onCollapsedChange ?? setInternalCollapsed;
  const isMobile = useIsMobile();

  const handleLinkClick = useCallback(() => {
    if (isMobile && onMobileOpenChange) {
      onMobileOpenChange(false);
    }
  }, [isMobile, onMobileOpenChange]);

  // Mobile: Sheet drawer
  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          side='left'
          className='w-64 p-0 bg-sidebar border-sidebar-border'
        >
          <SheetHeader className='sr-only'>
            <SheetTitle>{branding.name} Navigation</SheetTitle>
          </SheetHeader>
          <div className='flex h-full flex-col'>
            <SidebarContent
              branding={branding}
              navigation={navigation}
              basePath={basePath}
              singleExpanded={singleExpanded}
              user={user}
              onLogout={onLogout}
              collapsed={false}
              onLinkClick={handleLinkClick}
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Fixed sidebar
  return (
    <aside
      className={cn(
        'hidden md:flex h-screen flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <SidebarContent
        branding={branding}
        navigation={navigation}
        basePath={basePath}
        user={user}
        onLogout={onLogout}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        singleExpanded={singleExpanded}
      />
    </aside>
  );
}

export { DashboardSidebar, type DashboardSidebarProps, MobileSidebarTrigger };
